import React, { FC } from "react";
import { Stack, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { Callout, Image, Paragraph, CardGrid } from "@components/Content";
import {
  CalloutProps,
  HeadlineProps,
  ImageProps,
  ParagraphProps,
  CardGridProps,
  NumberCardRowsProps,
} from "@components/Content/types";
import { Colors } from "@theme/colors";
import HowItWorksHorizontal, {
  Props as HowItWorksHorizontalProps,
} from "../../components/HowItWorksHorizontal";
import NumberCardRows from "@src/components/Content/numbercards";

type UseCaseSection =
  | CardGridProps
  | HeadlineProps
  | ParagraphProps
  | CalloutProps
  | ImageProps
  | HowItWorksHorizontalProps
  | NumberCardRowsProps;

interface Props {
  sections: Array<UseCaseSection>;
}

const UseCase: FC<Props> = ({ sections }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("lg"));
  return (
    <Stack
      sx={{
        maxWidth: "1080px",
        margin: "0 auto",
        padding: { xs: "24px" },
      }}
    >
      {sections.map((section: UseCaseSection, idx: number) => {
        const sectionKey = `usecase-section-${idx}`;

        if (section.type === "paragraph") {
          return (
            <Paragraph
              content={section.content}
              key={sectionKey}
              containerSx={{
                maxWidth: "710px",
                margin: "40px auto 0",
                ...(section.containerStyle ?? {}),
              }}
              textSx={{
                fontSize: "18px",
                lineHeight: "24px",
                color: Colors.TextPrimary,
                textAlign: "center",
                ...(section.textStyle ?? {}),
              }}
            />
          );
        } else if (section.type === "headline") {
          const link = section.slug ? section.slug : sectionKey;
          return (
            <Typography
              key={sectionKey}
              variant={section.variant}
              sx={{
                color: Colors.HeadingText,
                textAlign: "center",
                marginTop: "32px",
                marginBottom: "24px",
              }}
            >
              {section.content}
            </Typography>
          );
        } else if (section.type === "callout") {
          return <Callout content={section.content} key={sectionKey} />;
        } else if (section.type === "image") {
          return (
            <Image
              url={section.url}
              width={section.width}
              height={section.height}
              caption={section.caption}
              center={section?.center}
              key={sectionKey}
            />
          );
        } else if (section.type === "cards") {
          return <CardGrid {...section} key={sectionKey} />;
        } else if (section.type === "numbercards") {
          return <NumberCardRows {...section} key={sectionKey} />;
        } else if (section.type === "how-it-works-horizontal") {
          return <HowItWorksHorizontal {...section} key={sectionKey} />;
        }
      })}
    </Stack>
  );
};

export default UseCase;
